import { useCallback, useEffect, useMemo, useState } from 'react';

import Checkbox from 'components/Checkbox';
import { InfoIcon } from 'components/icons';
import {Stats} from "components/icons";
import RenderIf from 'components/RenderIf';
import Tooltip from 'components/tooltip';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import {
  checkCirculationIsNotPlayed,
  idealGameOptionChooser,
  popularGameOptionChooser,
  predictionsMonitor,
  randomGameOptionChooser,
  ticketPriceCalculator,
} from '../../helpers';
import {
  createVoidTicket,
  getCirculationGames,
  getExactTicketsCount,
} from '../../requests';
import { GoMyTickets, InfoToast } from '../Toastify';

import './PlayCirculation.css';

const PlayCirculation = ({ circulations, onCreateVoidTicket }) => {
  const { t } = useTranslation();
  const [activeCirculationId, setActiveCirculationId] = useState(
    circulations?.[0]?.id
  );
  const [activeSelection, setActiveSelection] = useState(0);
  const [initialTicketGames, setInitialTicketGames] = useState([]);
  const [ticketGames, setTicketGames] = useState([]);
  const [ticketPrice, setTicketPrice] = useState(0);
  const [columnPrice, setColumnPrice] = useState();
  const [exactTicketCount, setExactTicketCount] = useState();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const circulation =
    useMemo(
      () =>
        circulations.find(
          (circulation) => circulation.id === activeCirculationId
        ),
      [circulations, activeCirculationId]
    ) ?? {};
  const navigate = useNavigate();

  const { isAuth } = useSelector((state) => ({
    isAuth: state.auth.accessToken !== undefined,
  }));
  const navigateCirculationTickets = () =>
    navigate(`/circulations/${circulation.id}/tickets`);

  const selectorHandler = useCallback(
    (id) => {
      const selectionsHandlers = {
        1: () => {
          if (checkCirculationIsNotPlayed(ticketGames)) {
            return toast.info(t('NO_TICKET_YET'));
          }

          const games = popularGameOptionChooser(initialTicketGames);
          const ticketPrice = ticketPriceCalculator(games, columnPrice);

          setTicketPrice(ticketPrice);
          setTicketGames(games);
        },
        2: () => {
          if (checkCirculationIsNotPlayed(ticketGames)) {
            return toast.info(t('NO_TICKET_YET'));
          }

          const games = idealGameOptionChooser(initialTicketGames);
          const ticketPrice = ticketPriceCalculator(games, columnPrice);

          setTicketPrice(ticketPrice);
          setTicketGames(games);
        },
        3: () => {
          const games = randomGameOptionChooser(initialTicketGames);
          const ticketPrice = ticketPriceCalculator(games, columnPrice);

          setTicketPrice(ticketPrice);
          setTicketGames(games);
        },
      };
      selectionsHandlers[id]();
      setActiveSelection(id);
    },
    [initialTicketGames, columnPrice, ticketGames]
  );

  function handleChangeCirculation(id) {
    if (id !== activeCirculationId) {
      setActiveCirculationId(id);
      setTicketGames([]);
      setInitialTicketGames([]);
      setActiveSelection(0);
    }
  }

  function handleResetSelections() {
    setTicketPrice(0);
    setActiveSelection(0);
    setTicketGames(initialTicketGames);
  }

  function handleChangeGameOption(e, index) {
    setActiveSelection(0);
    const games = ticketGames.map((game, gameIndex) => {
      if (gameIndex === index)
        return {
          ...game,
          [e.target.name]: e.target.checked,
        };
      return { ...game };
    });
    const ticketPrice = ticketPriceCalculator(games, columnPrice);
    setTicketPrice(ticketPrice);
    setTicketGames(games);
  }

  function handleSubmit() {
    setIsSubmitting(true);
    const areAllMatchesPredicted = predictionsMonitor(ticketGames);

    if (!isAuth) {
      navigate('/auth');
      return;
    }

    if (!areAllMatchesPredicted) {
      toast.info(InfoToast);
      setIsSubmitting(false);
    } else if (isAuth) {
      const games = ticketGames.map((game) => ({
        gameId: game.id,
        option1: game.option1,
        optionX: game.optionX,
        option2: game.option2,
      }));
      createVoidTicket(games, circulation.id, activeSelection)
        .then((res) => {
          toast.success(GoMyTickets);
          setIsSubmitting(false);
          onCreateVoidTicket({
            ticketId: res.data?.id,
            barcode: res.data?.ticketNumber,
            createdDate: res.data?.createdDate,
            price: res.data?.price,
          });
          handleResetSelections();
        })
        .catch((err) => {
          const message = err.response?.data?.message ?? 'Xəta baş verdi';
          toast.error(message);
          setIsSubmitting(false);
        });
    }
  }

  useEffect(() => {
    getCirculationGames(activeCirculationId)
      .then((res) => {
        const ticketGames =
          res.data?.games?.map((game) => ({
            ...game,
            option1: false,
            optionX: false,
            option2: false,
          })) || [];
        setColumnPrice(res.data?.columnPrice);
        setTicketGames(ticketGames);
        setInitialTicketGames(ticketGames);
      })
      .catch(() => null);
  }, [activeCirculationId]);

  useEffect(() => {
    const areAllMatchesPredicted = predictionsMonitor(ticketGames);
    if (areAllMatchesPredicted) {
      getExactTicketsCount(ticketGames, circulation.id)
        .then((res) => setExactTicketCount(res.data))
        .catch(() => null);
    }
  }, [ticketGames, circulation.id]);

  const selections = useMemo(
    () => [
      {
        id: 1,
        label: t('POPULAR'),
      },
      {
        id: 2,
        label: t('IDEAL'),
      },
      {
        id: 3,
        label: t('RANDOM_GAME'),
      },
    ],
    [t]
  );

  const tooltipData = useMemo(
    () => [
      {
        id: 1,
        title: t('WHAT_IS_POPULAR_TITLE'),
        description: t('WHAT_IS_POPULAR_DESCRIPTION'),
      },
      {
        id: 2,
        title: t('WHAT_IS_IDEAL_TITLE'),
        description: t('WHAT_IS_IDEAL_DESCRIPTION'),
      },
    ],
    [t]
  );

  return (
    <div className="circulation__wrapper">
      <div className="circulation">
        <div className="circulation__tabs">
          {circulations?.map((circulation, index) => (
            <button
              key={index}
              type="button"
              onClick={() => handleChangeCirculation(circulation.id)}
              className={`circulation__tab ${
                activeCirculationId === circulation.id
                  ? 'circulation__tab-active'
                  : ''
              }`}
            >
              {circulation.productName}
            </button>
          ))}
        </div>
        <div className="circulation__details">
          <div className="circulation__details-item">
            <span className="circulation__details-title">
              {t('CIRCULATION_NUMBER')}
            </span>
            <span className="circulation__details-description">
              {circulation.code}
            </span>
          </div>
          <div className="circulation__details-item">
            <span className="circulation__details-title">
              {t('POOL_AMOUNT')}
            </span>
            <span
              className={`circulation__details-description ${
                !circulation?.possibleSeeTotalAmount
                  ? 'circulation__details-blur'
                  : ''
              }`}
            >
              {circulation.totalAmount} AZN
            </span>
          </div>
          <div className="circulation__details-item">
            <span className="circulation__details-title">
              {t('ESTIMATED_POOL_AMOUNT')}
            </span>
            <span
                className={`circulation__details-description ${
                    !circulation?.possibleSeeTotalAmount
                        ? 'circulation__details-blur'
                        : ''
                }`}
            >
              1000000 AZN
            </span>
          </div>
          <div className="circulation__details-item">
            <span className="circulation__details-title">
              {t('BET_STOP_DATE')}
            </span>
            <span className="circulation__details-description circulation__details-description--blue">
              {circulation.expiredDate &&
                format(new Date(circulation.expiredDate), 'dd-MM-yyyy HH:mm')}
            </span>
          </div>
          <div className="circulation__details-item">
            <span className="circulation__details-title">
              {t('CIRCULATION_STOP_DATE')}
            </span>
            <span className="circulation__details-description circulation__details-description--red">
              {circulation.resultedDate &&
                format(new Date(circulation.resultedDate), 'dd-MM-yyyy HH:mm')}
            </span>
          </div>
          <button
            onClick={navigateCirculationTickets}
            className="circulation__details-button"
          >
            {t('TICKETS')}
          </button>
        </div>
        <div className="circulation__games">
          <div className="circulation__games-header">
            <div></div>
            <div>1</div>
            <div>x</div>
            <div>2</div>
          </div>
          <div className="circulation__games-body">
            {ticketGames.map((game, index) => (
              <div key={game.id} className="circulation__game">
                <div className="d-flex align-items-start gap-2">
                  <p>{index + 1}.</p>
                  <div className="circulation__game-text">

                    <p className="circulation__game-title">
                      <a target="_blank" href={game.statsLink} className="me-2"><Stats/></a>  {game.hostClubName} - {game.guestClubName}
                    </p>
                    <p className="circulation__game-date">
                      <span>
                        {format(new Date(game.startDate), 'dd-MM-yyyy')}
                      </span>
                      <span>{format(new Date(game.startDate), 'HH:mm')}</span>
                    </p>
                  </div>
                </div>

                <div className="d-flex gap-2 align-items-center circulation__game-selection">
                  <span className="mobile-option-name">1</span>
                  <Checkbox
                    name="option1"
                    checked={ticketGames[index]?.option1}
                    onChange={(e) => handleChangeGameOption(e, index)}
                  />
                  <span>
                    {Boolean(
                      game.option1Ratio ||
                        game.optionXRatio ||
                        game.option2Ratio
                    ) && `${game.option1Ratio}%`}
                  </span>
                </div>
                <div className="d-flex gap-2 align-items-center circulation__game-selection">
                  <span className="mobile-option-name">x</span>
                  <Checkbox
                    name="optionX"
                    checked={ticketGames[index]?.optionX}
                    onChange={(e) => handleChangeGameOption(e, index)}
                  />
                  <span>
                    {Boolean(
                      game.option1Ratio ||
                        game.optionXRatio ||
                        game.option2Ratio
                    ) && `${game.optionXRatio}%`}
                  </span>
                </div>
                <div className="d-flex gap-2 align-items-center circulation__game-selection">
                  <span className="mobile-option-name">2</span>
                  <Checkbox
                    name="option2"
                    checked={ticketGames[index]?.option2}
                    onChange={(e) => handleChangeGameOption(e, index)}
                  />
                  <span>
                    {Boolean(
                      game.option1Ratio ||
                        game.optionXRatio ||
                        game.option2Ratio
                    ) && `${game.option2Ratio}%`}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <RenderIf condition={Boolean(exactTicketCount)}>
        <div className="d-flex gap-2">
          <span className="fw-bold">{t('EXACT_TICKET_COUNT')}:</span>
          <span className="text-red">{exactTicketCount}</span>
        </div>
      </RenderIf>
      <div className="d-flex gap-2">
        <span className="fw-bold">{t('BET_AMOUNT_PER_COLUMN')}:</span>
        <span className="text-red">{columnPrice} AZN</span>
      </div>
      <div className="d-flex gap-2">
        <span className="fw-bold">{t('TOTAL_BET_AMOUNT')}:</span>
        <span className="text-red">
          {Boolean(ticketPrice) && `${ticketPrice} AZN`}
        </span>
      </div>
      <div className="toolbar">
        <div className="toolbar__inner">
          <div className="toolbar__quick-selections">
            <div className="toolbar__info">
              <span>{t('QUICK_BET_DESCRIPTION')}</span>
              <Tooltip data={tooltipData}>
                <InfoIcon />
              </Tooltip>
            </div>
            <div className="d-flex gap-2">
              {selections.map((button) => (
                <button
                  key={button.id}
                  onClick={() => selectorHandler(button.id)}
                  className={`toolbar__selection-button ${
                    button.id === activeSelection
                      ? 'toolbar__selection-button-active'
                      : ''
                  }`}
                >
                  {activeSelection === button.id && (
                    <svg className="animated-check" viewBox="0 0 24 24">
                      <path d="M4.1 12.7L9 17.6 20.3 6.3" fill="none" />
                    </svg>
                  )}
                  {button.label}
                </button>
              ))}
            </div>
          </div>
          <div className="d-flex gap-2">
            <button
              onClick={handleResetSelections}
              className="toolbar__button-light"
            >
              {t('CLEAR_PREDICTIONS')}
            </button>
            <button
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="toolbar__button"
            >
              {t('BRON')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlayCirculation;
